/**
 * quotationUtils.js — Single source of truth for quotation display + payload utilities.
 *
 * Created 2026-04-30 as part of Step 5B (Quotation Engine Stabilisation Sprint).
 * Consolidates duplicated inline helpers previously scattered across
 * CreateQuotation.js, QuotationDetail.js, and PreferencesStep.js.
 *
 * Python parity: applyMarkupDistribution is numerically identical to
 * apply_markup_distribution in backend/services/pdf_generator.py.
 */

// ============================================================================
// -1. Markup type <-> canonical mapping (2026-10-17)
// ============================================================================

/**
 * The wizard's UI model is `markup_type` ('percent'|'amount') + a separate
 * `markup_per_person` checkbox. The backend's canonical save schema expects
 * a single flat string: 'percent' | 'flat' | 'per_person'. Used when
 * building the Predefined/Custom-package SAVE payload so saves don't 422
 * on canonical validation.
 */
export function toCanonicalMarkupType(markupType, markupPerPerson) {
  if (!markupType || markupType === 'percent') return 'percent';
  return markupPerPerson ? 'per_person' : 'flat';
}

/**
 * Reverse of toCanonicalMarkupType — used when re-loading a saved/cloned
 * quotation's markup_type ('percent'|'flat'|'per_person', or legacy
 * 'amount') back into the wizard's (type, per_person) UI model.
 * `existingPerPerson` preserves the stored flag for legacy 'amount' rows
 * (saved before this mapping existed, where per_person was already its
 * own field rather than baked into markup_type).
 */
export function fromCanonicalMarkupType(markupType, existingPerPerson) {
  if (markupType === 'per_person') return { markup_type: 'amount', markup_per_person: true };
  if (markupType === 'flat') return { markup_type: 'amount', markup_per_person: false };
  if (markupType === 'amount') return { markup_type: 'amount', markup_per_person: !!existingPerPerson };
  return { markup_type: 'percent', markup_per_person: false };
}

// ============================================================================
// 0. vehicleUplift — one Uplift formula for wizard, saved view and PDF
// ============================================================================

/**
 * Markup-inclusive vehicle uplift vs the base vehicle — the PDF's rule:
 * total_quoted(option) − total_quoted(base). B2B responses carry a server
 * computed `uplift` (relative to the server's base) instead of total_quoted;
 * differences of either give the same delta, so the base can still be
 * re-picked client-side. `fallbackTotal(o)` covers lists with neither field
 * (e.g. raw transfer rates before Calculate) and should include markup.
 */
export function vehicleUplift(option, baseOption, fallbackTotal = () => 0) {
  const basis = (o) => {
    if (!o) return 0;
    if (o.total_quoted != null) return Number(o.total_quoted) || 0;
    if (o.uplift != null) return Number(o.uplift) || 0;
    return Number(fallbackTotal(o)) || 0;
  };
  return Math.round((basis(option) - basis(baseOption)) * 100) / 100;
}

// ============================================================================
// 1. applyMarkupDistribution — proportional markup split
// ============================================================================

/**
 * Proportionally distribute markup_amount across hotel_cost and
 * (transfer_cost + p2p_cost) so the displayed columns visually reconcile
 * to net_quoted_price without exposing a separate Markup column.
 *
 * Mirrors backend `apply_markup_distribution` in pdf_generator.py.
 *
 * @param {Object} pricing - Canonical category_pricing entry
 * @returns {{displayHotel: number, displayTransfer: number}}
 */
export function applyMarkupDistribution(pricing) {
  const hotel = Number(pricing?.hotel_cost || 0);
  const transfer = Number(pricing?.transfer_cost || 0);
  const p2p = Number(pricing?.p2p_cost || 0);
  const markup = Number(pricing?.markup_amount || 0);

  const transferTotal = transfer + p2p;
  const base = hotel + transferTotal;

  if (base <= 0 || markup === 0) {
    return { displayHotel: hotel, displayTransfer: transferTotal };
  }

  const hotelShare = hotel / base;
  const transferShare = transferTotal / base;
  return {
    displayHotel: Math.round((hotel + markup * hotelShare) * 100) / 100,
    displayTransfer: Math.round((transferTotal + markup * transferShare) * 100) / 100,
  };
}

// ============================================================================
// 2. getPricingBreakup — full derivation (future "Show Breakup" modal)
// ============================================================================

/**
 * Returns the full raw pricing derivation for a single category. Intended to
 * power a future "Show Breakup" modal on the Preview / Detail pages.
 * No UI consumes this today — stub in place so the modal component can be
 * wired up later without touching data plumbing.
 */
export function getPricingBreakup(pricing) {
  if (!pricing) return null;
  return {
    hotel_raw:   Number(pricing.hotel_cost || 0),
    transfer_raw: Number(pricing.transfer_cost || 0),
    p2p_raw:     Number(pricing.p2p_cost || 0),
    misc_raw:    Number(pricing.misc_cost || 0),
    gross:       Number(pricing.gross_cost || 0),
    markup:      Number(pricing.markup_amount || 0),
    discount:    Number(pricing.discount_amount || pricing.discount || 0),
    net:         Number(pricing.net_quoted_price || 0),
    gst_base:    Number(pricing.gst_base_amount || 0),
    gst:         Number(pricing.gst_amount || 0),
    total:       Number(pricing.total_with_gst || 0),
    per_person:  Number(pricing.per_person_price || 0),
  };
}

// ============================================================================
// 3. transformCategoriesForPricing — <MealPlanPricingTable> input shape
// ============================================================================

/**
 * Transform canonical category_pricing dict into the rows array that
 * MealPlanPricingTable expects. Hotel Cost and Transfers columns are
 * markup-apportioned via applyMarkupDistribution so visual math reconciles.
 *
 * Fallback chain is the UNION of behaviours from the two previous inline
 * versions (QuotationDetail.js and PreferencesStep.js) — no silent drop.
 *
 * @param {Object} categoryPricing - {CategoryName: pricingDict}
 * @param {Object} options
 * @param {Function} [options.filterFn] - (catName, pricing) => bool
 * @returns {Array<Object>}
 */
export function transformCategoriesForPricing(categoryPricing, options = {}) {
  const { filterFn, packageType } = options;
  if (!categoryPricing) return [];

  let entries = Object.entries(categoryPricing);
  if (filterFn) entries = entries.filter(([cat, pricing]) => filterFn(cat, pricing));

  // BUG-13 — for DMC, the override cost IS the agency's procurement base
  // and the markup is the agency margin. Spec says both must be visible as
  // distinct values, NOT smeared into Hotel Cost via markup distribution.
  // For non-DMC types we keep the proportional distribution that lets the
  // visible Hotel + Transfer columns sum to net_quoted_price.
  const isDmc = packageType === 'dmc' || packageType === 'dmc_package';

  return entries.map(([catName, pricing]) => {
    const normalised = {
      ...pricing,
      hotel_cost: pricing.hotel_cost || pricing.accommodation_total || 0,
      transfer_cost: pricing.transfer_cost || 0,
      p2p_cost: pricing.p2p_cost || 0,
      markup_amount: pricing.markup_amount || 0,
    };
    const { displayHotel, displayTransfer } = isDmc
      ? { displayHotel: normalised.hotel_cost, displayTransfer: normalised.transfer_cost }
      : applyMarkupDistribution(normalised);

    return {
      name: catName,
      hotelCost:        displayHotel,
      transfers:        displayTransfer,
      totalPackage:     pricing.net_quoted_price || pricing.total_package_price || pricing.grand_total || pricing.total_cost || 0,
      net_quoted_price: pricing.net_quoted_price || 0,
      discount:         pricing.discount_amount || pricing.discount || 0,
      markup_amount:    pricing.markup_amount || 0,
      gst_amount:       pricing.gst_amount || 0,
      total_with_gst:   pricing.total_with_gst || pricing.total_cost || 0,
      adultPrice:       pricing.adult_price || pricing.per_person_price || 0,
      extraBedPrice:    pricing.adult_extra_bed_price || 0,
      childBedPrice:    pricing.child_with_bed_price || 0,
      childNoBedPrice:  pricing.child_no_bed_price || 0,
      infantPrice:      pricing.infant_price || 0,
    };
  });
}

// ============================================================================
// 4. buildPaxConfig — canonical pax shape for display components
// ============================================================================

export function buildPaxConfig(formData) {
  const rc = formData?.room_config || {};
  return {
    adults:        formData?.no_adults || 0,
    extraBed:      rc.adult_extra_beds || 0,
    childWithBed:  formData?.no_children_with_bed || 0,
    childNoBed:    formData?.no_children_no_bed || 0,
    infants:       formData?.no_infants || 0,
  };
}

// ============================================================================
// 5. buildGstConfig — canonical GST shape for display components
// ============================================================================

export function buildGstConfig(formData) {
  return {
    percentage: formData?.gst_percentage || 0,
    included:   formData?.gst_included !== false,
    applyOn:    formData?.gst_apply_on || 'total',
  };
}

// ============================================================================
// 6. resolvePlaceName — place name lookup with graceful fallback
// ============================================================================

/**
 * @param {Object|string} stop - Route stop (object with place_id/place_name) or raw string
 * @param {Array} [places] - Optional places list for id lookup
 */
export function resolvePlaceName(stop, places = []) {
  if (!stop) return '';
  if (typeof stop === 'string') return stop;
  if (stop.place_name) return stop.place_name;
  if (stop.name) return stop.name;
  if (stop.place_id && Array.isArray(places)) {
    const match = places.find((p) => p.place_id === stop.place_id);
    if (match) return match.place_name || match.name || '';
  }
  return '';
}

// ============================================================================
// 7. buildCalculationRequest — single source for /calculate-* payloads
// ============================================================================

/**
 * Build the payload for /api/v1/quotations/calculate* endpoints.
 * Single source of truth replacing 4 inline payload branches previously in
 * CreateQuotation.js. Each branch reproduces the exact legacy shape.
 *
 * Returns an object { endpoint, payload } — caller POSTs payload to endpoint.
 *
 * @param {Object} formData - Full wizard form state
 * @param {Object} extras - { stopsWithHotels?: Array } - pre-computed helpers from caller
 * @returns {{endpoint: string, payload: Object}}
 */
export function buildCalculationRequest(formData, extras = {}) {
  const routeType = formData.route_type;

  // -------- DMC Package --------
  if (routeType === 'dmc_package') {
    const payload = {
      route_type: 'dmc_package',
      dmc_content: formData.dmc_content,
      arrival_city: formData.arrival_city || '',
      departure_city: formData.departure_city || '',
      tour_start_date: formData.arrival_date || null,
      no_adults: formData.no_adults || 2,
      no_children_with_bed: formData.no_children_with_bed || 0,
      no_children_no_bed: formData.no_children_no_bed || 0,
      no_infants: formData.no_infants || 0,
      room_config: formData.room_config || {
        auto_calculate: true,
        single_rooms: 0,
        double_rooms: 1,
        triple_rooms: 0,
      },
      markup_type: formData.markup_type || 'percent',
      markup_pct: formData.markup_type === 'percent' ? (formData.markup_pct || 0) : 0,
      markup_amount: formData.markup_type === 'amount' ? (formData.markup_amount || 0) : 0,
      markup_per_person: formData.markup_per_person || false,
      discount_type: formData.discount_type || 'percent',
      discount_pct: formData.discount_type === 'percent' ? (formData.discount_pct || 0) : 0,
      discount_amount: formData.discount_type === 'amount' ? (formData.discount_amount || 0) : 0,
      discount_per_person: formData.discount_per_person || false,
      gst_percentage: formData.gst_percentage || 0,
      gst_included: formData.gst_included !== false,
      gst_apply_on: formData.gst_apply_on || 'total',
      quotation_format: formData.quotation_format || 'total',
      misc_costs: formData.misc_costs || [],
    };
    return { endpoint: '/quotations/calculate', payload };
  }

  // -------- Manual Package --------
  if (routeType === 'manual_package') {
    const payload = {
      route_type: 'manual_package',
      no_adults: formData.no_adults || 2,
      no_children_with_bed: formData.no_children_with_bed || 0,
      no_children_no_bed: formData.no_children_no_bed || 0,
      arrival_date: formData.arrival_date,
      markup_type: formData.markup_type || 'percent',
      markup_pct: parseFloat(formData.markup_pct) || 0,
      markup_amount: parseFloat(formData.markup_amount) || 0,
      markup_per_person: formData.markup_per_person || false,
      discount_type: formData.discount_type || 'percent',
      discount_pct: parseFloat(formData.discount_pct) || 0,
      discount_amount: parseFloat(formData.discount_amount) || 0,
      discount_per_person: formData.discount_per_person || false,
      gst_percentage: parseFloat(formData.gst_percentage) || 0,
      gst_included: formData.gst_included || false,
      gst_apply_on: formData.gst_apply_on || 'total',
      room_config: formData.room_config || { single_rooms: 0, double_rooms: 1, triple_rooms: 0 },
      manual_package_data: formData.manual_package_data || {},
      p2p_transfers: formData.p2p_transfers || [],
      misc_costs: formData.misc_costs || [],
    };
    return { endpoint: '/quotations/calculate', payload };
  }

  // -------- Custom Package (hotel-selector flow) --------
  // One price tier per option (`options[]`). Vehicle, transfer days, P2P,
  // misc costs, markup, discount and GST are SHARED across options — the
  // operator sets them once in Step 3.
  if (routeType === 'custom_package') {
    const pkg = formData.custom_package_data || {};
    const options = extras.options || [];
    const stopsWithHotels = options[0]?.stops || extras.stopsWithHotels || [];
    const resolvedVehicleType =
      formData.vehicle_type_selected || formData.base_vehicle_type || pkg?.vehicle_type || '';
    const autoTransferDays =
      (stopsWithHotels.reduce((sum, s) => sum + (s.nights || 0), 0) || 0) + 1;
    const resolvedTransferDays =
      formData.transfer_days_auto === false
        ? (formData.transfer_days || autoTransferDays)
        : autoTransferDays;

    const payload = {
      options,
      stops: stopsWithHotels,
      vehicle_type: resolvedVehicleType,
      vehicle_counts: formData.vehicle_counts || {},
      transfer_days: resolvedTransferDays,
      // Bug C3 fix (2026-04-30): default arrival_city / departure_city to empty
      // strings so a missing custom_package_data field does not surface as
      // "Field required, Field required" Pydantic toast when the user is just
      // toggling misc-cost rows. Backend treats blank as ignored.
      arrival_city: pkg?.arrival_city || formData.arrival_city || '',
      departure_city: pkg?.departure_city || formData.departure_city || pkg?.arrival_city || formData.arrival_city || '',
      no_adults: formData.no_adults,
      no_children_with_bed: formData.no_children_with_bed,
      no_children_no_bed: formData.no_children_no_bed,
      no_infants: formData.no_infants,
      room_config: {
        single_rooms: formData.single_rooms || 0,
        // `??` not `||`: auto-calc can legitimately give 0 double rooms (e.g. 1 adult
        // -> single room); `|| 1` used to add a phantom double room.
        double_rooms: formData.double_rooms ?? 1,
        triple_rooms: formData.triple_rooms || 0,
        extra_beds: formData.extra_beds || 0,
        // Raw wizard keys, so the backend can derive extra beds if `extra_beds` is absent.
        adult_extra_beds: formData.room_config?.adult_extra_beds || 0,
        children_with_bed: formData.room_config?.children_with_bed ?? formData.no_children_with_bed ?? 0,
      },
      markup_type: formData.markup_type || 'percent',
      markup_pct: formData.markup_type === 'amount' ? 0 : (formData.markup_pct || 0),
      markup_amount: formData.markup_type === 'amount' ? (formData.markup_amount || 0) : 0,
      markup_per_person: formData.markup_per_person || false,
      discount_type: formData.discount_type || 'percent',
      discount_pct:
        formData.discount_type === 'amount' ? 0 : (formData.discount_pct || 0),
      discount_amount: formData.discount_type === 'amount' ? (formData.discount_amount || 0) : 0,
      discount_per_person: formData.discount_per_person || false,
      gst_percentage: formData.gst_percentage || 0,
      gst_included: formData.gst_included || false,
      gst_apply_on: formData.gst_apply_on || 'total',
      quotation_format: formData.quotation_format || 'total',
      misc_costs: formData.misc_costs || [],
      p2p_transfers: formData.p2p_transfers || [],
    };
    return { endpoint: '/quotations/calculate-custom-package', payload };
  }

  // -------- Predefined / Custom (route-based) --------
  const payload = {
    route_id: routeType === 'predefined' ? formData.route_id : null,
    custom_route:
      routeType === 'custom'
        ? (formData.custom_route || []).map((stop, idx) => ({
            place_id: stop.place_id,
            place_name: stop.place_name || stop.place || '',
            nights: stop.nights || 1,
            order_seq: stop.order_seq || idx + 1,
          }))
        : null,
    arrival_city: formData.arrival_city || '',
    arrival_point: formData.arrival_point || 'Airport',
    arrival_date: formData.arrival_date,
    departure_city: formData.departure_city || formData.arrival_city || '',
    tour_start_date: formData.arrival_date,
    no_adults: formData.no_adults || 2,
    no_children_with_bed: formData.no_children_with_bed || 0,
    no_children_no_bed: formData.no_children_no_bed || 0,
    no_infants: formData.no_infants || 0,
    hotel_category: formData.selected_categories?.[0] || 'Deluxe',
    selected_categories:
      formData.selected_categories?.length > 0 ? formData.selected_categories : ['Deluxe'],
    classification_mode: formData.classification_mode || 'PRICE_RANGE',
    meal_plan: formData.meal_plans?.[0] || 'CP',
    meal_plans: formData.meal_plans?.length > 0 ? formData.meal_plans : ['CP'],
    vehicle_type_selected: formData.vehicle_type_selected || formData.base_vehicle_type || null,
    markup_pct: formData.markup_type === 'percent' ? (formData.markup_pct || 0) : 0,
    markup_type: formData.markup_type || 'percent',
    markup_amount: formData.markup_type === 'amount' ? (formData.markup_amount || 0) : 0,
    markup_per_person: formData.markup_per_person || false,
    discount_pct:
      formData.discount_type === 'percent' || !formData.discount_type
        ? (formData.discount_pct || 0)
        : 0,
    discount_type: formData.discount_type || 'percent',
    discount_amount: formData.discount_type === 'amount' ? (formData.discount_amount || 0) : 0,
    discount_per_person: formData.discount_per_person || false,
    gst_percentage: formData.gst_percentage || 0,
    gst_included: formData.gst_included !== false,
    gst_apply_on: formData.gst_apply_on || 'total',
    show_breakup: formData.show_breakup !== false,
    quotation_format: formData.quotation_format || 'total',
    room_config: formData.room_config || {
      auto_calculate: true,
      single_rooms: formData.single_rooms || 0,
      double_rooms: formData.double_rooms || 1,
      triple_rooms: formData.triple_rooms || 0,
      adult_extra_beds: 0,
    },
    hotel_overrides: formData.hotel_overrides || null,
    base_vehicle_type: formData.base_vehicle_type || null,
  };
  return { endpoint: '/quotations/calculate', payload };
}
